import type { AxiosResponse } from 'axios';
import { BACKEND_ERROR_CODE, REQUEST_LANGUAGE, createFlatRequest, createRequest } from '@sa/axios';
import { router } from '@/router';
import { useAuthStore } from '@/store/modules/auth';
import { useVersionStore } from '@/store/modules/version';
import { useOrgStore } from '@/store/modules/org';
import { localStg } from '@/utils/storage';
import { getServiceBaseURL } from '@/utils/service';
import { $t } from '@/locales';
import { getAuthorization, getLanguage, handleExpiredRequest, showErrorMsg } from './shared';
import type { RequestInstanceState } from './type';

const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
const { baseURL, otherBaseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);

// 基础请求配置
const baseRequestConfig = {
  baseURL,
  async onRequest(config) {
    const Authorization = getAuthorization();
    const language = getLanguage();

    Object.assign(config.headers, { Authorization, [REQUEST_LANGUAGE]: language });

    // 打印请求详细信息
    // console.log('请求配置:', {
    //   url: config.url,
    //   baseURL: config.baseURL,
    //   fullURL: `${config.baseURL}${config.url}`,
    //   method: config.method,
    //   headers: config.headers,
    //   params: config.params,
    //   data: config.data
    // });

    return config;
  },
  isBackendSuccess(response) {
    // when the backend response code is "0000"(default), it means the request is success
    // to change this logic by yourself, you can modify the `VITE_SERVICE_SUCCESS_CODE` in `.env` file
    return String(response.data.code) === import.meta.env.VITE_SERVICE_SUCCESS_CODE;
  },
  async onBackendFail(response, instance) {
    const authStore = useAuthStore();
    const responseCode = String(response.data.code);
    const responseMessage = response.data.message || '';

    function handleLogout() {
      authStore.resetStore();
    }

    function logoutAndCleanup() {
      handleLogout();
      window.removeEventListener('beforeunload', handleLogout);
      request.state.errMsgStack = request.state.errMsgStack.filter(msg => msg !== response.data.message);
    }

    // 特殊处理：检测"您的账号在其他地方登录"的情况
    if (responseCode === '401' && responseMessage.includes('您的账号在其他地方登录')) {
      window.$message?.error('检测到账号在其他地方登录，正在跳转到登录页面...');
      handleLogout();
      return null;
    }

    // when the backend response code is in `logoutCodes`, it means the user will be logged out and redirected to login page
    const logoutCodes = import.meta.env.VITE_SERVICE_LOGOUT_CODES?.split(',') || [];
    if (logoutCodes.includes(responseCode)) {
      handleLogout();
      return null;
    }

    // when the backend response code is in `modalLogoutCodes`, it means the user will be logged out by displaying a modal
    const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
    if (modalLogoutCodes.includes(responseCode) && !request.state.errMsgStack?.includes(response.data.message)) {
      request.state.errMsgStack = [...(request.state.errMsgStack || []), response.data.message];

      // prevent the user from refreshing the page
      window.addEventListener('beforeunload', handleLogout);

      window.$dialog?.error({
        title: $t('common.error'),
        content: response.data.message,
        positiveText: $t('common.confirm'),
        maskClosable: false,
        closeOnEsc: false,
        onPositiveClick() {
          logoutAndCleanup();
        },
        onClose() {
          logoutAndCleanup();
        }
      });

      return null;
    }

    // when the backend response code is in `expiredTokenCodes`, it means the token is expired, and refresh token
    // the api `refreshToken` can not return error code in `expiredTokenCodes`, otherwise it will be a dead loop, should return `logoutCodes` or `modalLogoutCodes`
    const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
    if (expiredTokenCodes.includes(responseCode)) {
      const success = await handleExpiredRequest(request.state);
      if (success) {
        const Authorization = getAuthorization();
        const language = getLanguage();
        Object.assign(response.config.headers, { Authorization, [REQUEST_LANGUAGE]: language });

        return instance.request(response.config) as Promise<AxiosResponse>;
      }
    }

    return null;
  },
  transformBackendResponse(response) {
    return response.data.data;
  },
  onError(error) {
    // when the request is fail, you can show error message

    let message = error.message;
    let backendErrorCode = '';

    // get backend error message and code
    if (error.code === BACKEND_ERROR_CODE) {
      message = error.response?.data?.message || message;
      backendErrorCode = String(error.response?.data?.code) || '';
    }

    // 特殊处理：检测"您的账号在其他地方登录"的情况
    // 需要同时检查 HTTP 状态码 401 和 JSON body 中的 code/message
    const httpStatus = error.response?.status;
    const responseCode = String(error.response?.data?.code || '');
    const responseMessage = error.response?.data?.message || '';

    if (
      (httpStatus === 401 || responseCode === '401') &&
      (message.includes('您的账号在其他地方登录') || responseMessage.includes('您的账号在其他地方登录'))
    ) {
      const authStore = useAuthStore();
      window.$message?.error('检测到账号在其他地方登录，正在跳转到登录页面...');
      authStore.resetStore();
      return;
    }

    // the error message is displayed in the modal
    const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
    if (modalLogoutCodes.includes(backendErrorCode)) {
      return;
    }

    // when the token is expired, refresh token and retry request, so no need to show error message
    const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
    if (expiredTokenCodes.includes(backendErrorCode)) {
      return;
    }

    showErrorMsg(request.state, message);
  }
};

// 缓存当前路由路径，用于处理路由切换时的异步请求
let cachedRoutePath = '';

// 安全地监听路由变化，更新缓存
const initRouterGuard = () => {
  try {
    const { router } = import.meta.glob('@/router/index.ts', { eager: true }) as any;
    if (router) {
      router.afterEach((to: any) => {
        cachedRoutePath = to.path;
      });
    }
  } catch (error) {
    console.warn('路由监听初始化失败，将在versionRequest中动态获取路由:', error);
  }
};
initRouterGuard();

// 不带版本信息的请求
export const request = createFlatRequest<App.Service.Response, RequestInstanceState>({ baseURL }, baseRequestConfig);

// 带版本信息的请求
export const versionRequest = createFlatRequest<App.Service.Response, RequestInstanceState>(
  { baseURL },
  {
    ...baseRequestConfig,
    async onRequest(config) {
      const Authorization = getAuthorization();
      const language = getLanguage();
      const versionStore = useVersionStore();
      const userId = versionStore.userId; // version 改为userId

      const orgStore = useOrgStore();
      const currentOrgCode = orgStore.currentOrgCode || orgStore.defaultOrgCode;
      const currentDeptCode = orgStore.currentDeptCode || '';

      // 获取auth store来检查用户角色
      const authStore = useAuthStore();

      // 检查用户是否为管理员 -- 后期需要给不同的角色分配不同的角色id -- 后期修改
      const isRSuper = authStore.userInfo.roleIds.includes('R_SUPER');
      const isRAdmin = authStore.userInfo.roleIds.includes('R_ADMIN');
      const isAdmin = authStore.userInfo.roleIds.includes('ADMIN');

      // 使用超级管理员角色进行判断
      const isSuperAdmin = authStore.userInfo.userName === 'admin';

      // 配置需要设置为BASE版本的完整路由路径字典
      const baseGoodsRoutes: Record<string, boolean> = {
        '/base-goods': true,
        '/base-goods/appointment': true, // 预约业务管理
        '/base-goods/inbound': true, // 采购业务管理
        '/base-goods/outbound': true, // 销售业务管理
        '/base-goods/inbound-images': true, // 采购影像管理
        '/base-goods/inbound-weight': true, // 采购过磅单管理
        '/base-goods/inbound-quality': true, // "采购质检单管理"
        '/base-goods/purchase-waybill': true, // "采购运单管理"
        '/base-goods/outbound-images': true, // "销售影像管理"
        '/base-goods/outbound-weight': true, // "销售过磅单管理"
        '/base-goods/outbound-quality': true, // "销售质检单管理"
        '/base-goods/sales-waybill': true, // "销售运单管理"
        '/settlement/purchase-settlement-center': true, // 采购结算中心
        '/settlement/sales-settlement-center': true, // 销售结算中心
        '/approval': true // 审批工作台
        // 这里可以添加其他需要设置为BASE的完整路由路径
        // 例如: '/base-goods/detail': true,
        //       '/base-goods/list': true
      };

      // 检查当前路由是否在字典中（完整路径匹配）
      let currentRoute: any = null;
      try {
        currentRoute = router.currentRoute.value;
      } catch (e) {
        // 忽略初始化前的访问错误
      }
      let routePath = currentRoute?.path || '';

      // 如果当前路由路径为空，尝试从window.location获取或使用缓存
      if (!routePath || routePath === '/') {
        if (cachedRoutePath) {
          routePath = cachedRoutePath;
        } else {
          routePath = window.location.pathname || '';
        }
      }

      const isBaseGoodsRoute = Boolean(baseGoodsRoutes[routePath]);

      // 调试信息
      console.log('=== 版本请求调试信息 ===');
      console.log('当前路由路径:', routePath);
      console.log('请求URL:', config.url);
      console.log('是否匹配BASE路由:', isBaseGoodsRoute);
      console.log('字典中的路由:', Object.keys(baseGoodsRoutes));

      // 基础请求头
      const headers: Record<string, string> = {
        Authorization: Authorization || '',
        [REQUEST_LANGUAGE]: language,
        'X-Tenant-ID': userId || '',
        'X-Tenant-VERSION': isBaseGoodsRoute ? 'BASE' : 'NON-BASE',
        'X-USER-ID': versionStore.userId || '',
        'X-DEPT-CODE': currentDeptCode || ''
      };

      // 调试信息
      console.log('设置的X-Tenant-VERSION:', headers['X-Tenant-VERSION']);
      console.log('=====================');

      // 只有非超级管理员用户才设置X-ORG-CODE
      if (!isSuperAdmin) {
        headers['X-ORG-CODE'] = currentOrgCode || '';
      } else {
        // headers['X-ORG-CODE'] = currentOrgCode || '';
      }

      Object.assign(config.headers, headers);

      return config;
    }
  }
);

export const demoRequest = createRequest<App.Service.DemoResponse>(
  {
    baseURL: otherBaseURL.demo
  },
  {
    async onRequest(config) {
      const { headers } = config;

      // set token
      const token = localStg.get('token');
      const Authorization = token ? `Bearer ${token}` : null;
      Object.assign(headers, { Authorization });

      return config;
    },
    isBackendSuccess(response) {
      // when the backend response code is "200", it means the request is success
      // you can change this logic by yourself
      return response.data.status === '200';
    },
    async onBackendFail(_response) {
      // when the backend response code is not "200", it means the request is fail
      // for example: the token is expired, refresh token and retry request
    },
    transformBackendResponse(response) {
      return response.data.result;
    },
    onError(error) {
      // when the request is fail, you can show error message

      let message = error.message;

      // show backend error message
      if (error.code === BACKEND_ERROR_CODE) {
        message = error.response?.data?.message || message;
      }

      window.$message?.error(message);
    }
  }
);
